import React, { useEffect, useState } from 'react';
import api from '../api';
import DatePicker from '../components/DatePicker';
import {
  Utensils,
  Flame,
  Drumstick,
  Wheat,
  Droplet,
  Pencil,
  Trash2,
  Plus,
  X,
  Calendar,
  Filter,
} from 'lucide-react';

const Meals = () => {
  const [meals, setMeals] = useState([]);
  const [form, setForm] = useState({ name: '', calories: '', protein: '', carbs: '', fat: '' });
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null); // null means "All meals"
  const [viewMode, setViewMode] = useState('all'); // 'all' or 'date'
  
  const fetchMeals = async (date = null) => {
    try {
      let url = '/meals';
      if (date) {
        // Fetch meals for specific date
        url = `/meals?date=${date}`;
      } else {
        // Fetch all meals
        url = '/meals/all';
      }
      
      const res = await api.get(url);
      if (Array.isArray(res.data)) {
        setMeals(res.data);
      } else {
        const { food = [], exercise = [] } = res.data;
        setMeals([...food, ...exercise]);
      }
    } catch (err) {
      setError('Failed to fetch meals');
    }
  };
  useEffect(() => {
    if (viewMode === 'all') {
      fetchMeals(); // Fetch all meals
    } else {
      fetchMeals(selectedDate); // Fetch meals for selected date
    }
  }, [selectedDate, viewMode]);

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setViewMode('date');
  };

  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    if (mode === 'all') {
      setSelectedDate(null);
    } else if (mode === 'date' && !selectedDate) {
      // Default to today if switching to date mode without a selected date
      const today = new Date();
      const todayIso = today.getFullYear() + '-' + String(today.getMonth() + 1).padStart(2, '0') + '-' + String(today.getDate()).padStart(2, '0');
      setSelectedDate(todayIso);
    }
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      if (editingId) {
        await api.put(`/meals/${editingId}`, form);
      } else {
        await api.post('/meals', form);
      }      setForm({ name: '', calories: '', protein: '', carbs: '', fat: '' });
      setEditingId(null);
      setShowModal(false);
      // Refresh meals based on current view mode
      if (viewMode === 'all') {
        fetchMeals();
      } else {
        fetchMeals(selectedDate);
      }
    } catch (err) {
      setError('Failed to save meal');
    }
  };

  const handleEdit = (meal) => {
    setForm({
      name: meal.name,
      calories: meal.calories,
      protein: meal.protein,
      carbs: meal.carbs,
      fat: meal.fat,
    });
    setEditingId(meal._id);
    setShowModal(true);
  };
  const handleDelete = async (id) => {
    try {
      await api.delete(`/meals/${id}`);
      // Refresh meals based on current view mode
      if (viewMode === 'all') {
        fetchMeals();
      } else {
        fetchMeals(selectedDate);
      }
    } catch (err) {
      setError('Failed to delete meal');
    }
  };
  return (
    <div className="meals-page min-h-screen bg-gradient-to-br from-gray-50 to-emerald-50/30">
      {/* Header Section */}
      <div className="bg-gradient-to-r from-emerald-500 to-teal-500 shadow-lg">
        <div className="max-w-4xl mx-auto px-6 py-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">Your Meals</h1>
              <p className="text-emerald-100">Manage your custom meal database</p>
            </div>
            <button
              onClick={() => setShowModal(true)}
              className="flex items-center gap-2 bg-white/20 hover:bg-white/30 backdrop-blur-sm border border-white/30 text-white px-6 py-3 rounded-xl font-medium transition-all duration-200 hover:shadow-lg hover:scale-105 active:scale-95"
            >
              <Plus size={20} /> Add Meal
            </button>
          </div>
        </div>      </div>

      {/* Filter Section */}
      <div className="max-w-4xl mx-auto px-6 py-4">
        <div className="bg-white/80 backdrop-blur-sm border border-emerald-100/50 rounded-2xl p-6 mb-6">
          <div className="flex flex-col space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <Filter className="text-emerald-600" size={20} />
              <h3 className="text-lg font-semibold text-gray-800">Filter Meals</h3>
            </div>
            
            {/* View Mode Toggle */}
            <div className="flex gap-3">
              <button
                onClick={() => handleViewModeChange('all')}
                className={`px-4 py-2 rounded-xl font-medium transition-all duration-200 ${
                  viewMode === 'all'
                    ? 'bg-emerald-500 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                All Meals
              </button>
              <button
                onClick={() => handleViewModeChange('date')}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium transition-all duration-200 ${
                  viewMode === 'date'
                    ? 'bg-emerald-500 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <Calendar size={16} />
                By Date
              </button>
            </div>
            
            {/* Date Picker - Only show when in date mode */}
            {viewMode === 'date' && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Date:
                </label>
                <DatePicker
                  selectedDate={selectedDate}
                  onSelect={handleDateSelect}
                />
              </div>
            )}
            
            {/* Results Info */}
            <div className="flex items-center justify-between pt-4 border-t border-gray-100">
              <div className="text-sm text-gray-600">
                {viewMode === 'all' ? (
                  `Showing all meals (${meals.length} total)`
                ) : (                  `Showing meals for ${selectedDate ? (() => {
                    const [year, month, day] = selectedDate.split('-').map(Number);
                    const localDate = new Date(year, month - 1, day);
                    return localDate.toLocaleDateString('en-US', { 
                      weekday: 'long', 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    });
                  })() : 'selected date'} (${meals.length} meals)`
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="max-w-4xl mx-auto p-6">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-6 flex items-center gap-2">
            <X size={18} />
            {error}
          </div>
        )}

        {meals.length === 0 ? (
          <div className="text-center py-12">
            <Utensils className="mx-auto text-gray-400 mb-4" size={48} />
            <h3 className="text-xl font-semibold text-gray-600 mb-2">No meals yet</h3>
            <p className="text-gray-500 mb-6">Start building your meal database by adding your first meal.</p>
            <button
              onClick={() => setShowModal(true)}
              className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white px-6 py-3 rounded-xl font-medium transition-all duration-200 hover:shadow-lg"
            >
              <Plus size={20} /> Add Your First Meal
            </button>
          </div>
        ) : (
          <div className="grid gap-4">
            {meals.map((meal) => (
              <div key={meal._id} className="bg-white/80 backdrop-blur-sm border border-emerald-100/50 hover:border-emerald-200 p-6 rounded-2xl shadow-sm hover:shadow-lg transition-all duration-200 group">
                <div className="flex justify-between items-start mb-4">                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      meal.isExercise 
                        ? 'bg-gradient-to-br from-orange-500 to-red-500' 
                        : 'bg-gradient-to-br from-emerald-500 to-teal-500'
                    }`}>
                      {meal.isExercise ? (
                        <Flame className="text-white" size={20} />
                      ) : (
                        <Utensils className="text-white" size={20} />
                      )}
                    </div><div>
                      <h3 className="text-xl font-semibold text-gray-800 group-hover:text-emerald-700 transition-colors">
                        {meal.name}
                      </h3>
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <span>{meal.isExercise ? 'Exercise' : 'Food'}</span>
                        <span>•</span>
                        <span>{new Date(meal.date).toLocaleDateString('en-US', { 
                          weekday: 'short', 
                          month: 'short', 
                          day: 'numeric',
                          year: 'numeric'
                        })}</span>
                        <span>•</span>
                        <span>{new Date(meal.date).toLocaleTimeString('en-US', { 
                          hour: '2-digit', 
                          minute: '2-digit'
                        })}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button 
                      onClick={() => handleEdit(meal)} 
                      className="w-10 h-10 flex items-center justify-center bg-blue-50 hover:bg-blue-100 text-blue-600 hover:text-blue-700 rounded-xl transition-all duration-200 hover:scale-105"
                      title="Edit meal"
                    >
                      <Pencil size={18} />
                    </button>
                    <button 
                      onClick={() => handleDelete(meal._id)} 
                      className="w-10 h-10 flex items-center justify-center bg-red-50 hover:bg-red-100 text-red-600 hover:text-red-700 rounded-xl transition-all duration-200 hover:scale-105"
                      title="Delete meal"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
                  <div className={`grid gap-4 ${meal.isExercise ? 'grid-cols-1 md:grid-cols-1' : 'grid-cols-2 md:grid-cols-4'}`}>
                  <div className="flex items-center gap-3 bg-orange-50 p-3 rounded-xl">
                    <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                      <Flame className="text-orange-600" size={16} />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">{meal.isExercise ? 'Calories Burned' : 'Calories'}</p>
                      <p className="font-semibold text-gray-800">{meal.calories}</p>
                    </div>
                  </div>
                  
                  {!meal.isExercise && (
                    <>
                      <div className="flex items-center gap-3 bg-purple-50 p-3 rounded-xl">
                        <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                          <Drumstick className="text-purple-600" size={16} />
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Protein</p>
                          <p className="font-semibold text-gray-800">{meal.protein}g</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-3 bg-yellow-50 p-3 rounded-xl">
                        <div className="w-8 h-8 bg-yellow-100 rounded-lg flex items-center justify-center">
                          <Wheat className="text-yellow-600" size={16} />
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Carbs</p>
                          <p className="font-semibold text-gray-800">{meal.carbs}g</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-3 bg-pink-50 p-3 rounded-xl">
                        <div className="w-8 h-8 bg-pink-100 rounded-lg flex items-center justify-center">
                          <Droplet className="text-pink-600" size={16} />
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Fat</p>
                          <p className="font-semibold text-gray-800">{meal.fat}g</p>
                        </div>
                      </div>                    </>
                  )}
                </div>
                
                {/* Additional Info */}
                {(meal.serving_size || meal.description) && (
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    {meal.serving_size && (
                      <div className="mb-2">
                        <span className="text-sm font-medium text-gray-600">Serving Size: </span>
                        <span className="text-sm text-gray-800">{meal.serving_size}</span>
                      </div>
                    )}
                    {meal.description && (
                      <div>
                        <span className="text-sm font-medium text-gray-600">Description: </span>
                        <span className="text-sm text-gray-800">{meal.description}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white/95 backdrop-blur-xl border border-emerald-200/50 rounded-2xl p-8 w-full max-w-2xl relative shadow-2xl">
            <button
              onClick={() => {
                setShowModal(false);
                setForm({ name: '', calories: '', protein: '', carbs: '', fat: '' });
                setEditingId(null);
              }}
              className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-red-100 text-gray-600 hover:text-red-600 rounded-xl transition-all duration-200 hover:scale-105"
            >
              <X size={20} />
            </button>
            
            <div className="mb-6">
              <h3 className="text-2xl font-bold text-gray-800 mb-2">
                {editingId ? 'Edit Meal' : 'Add New Meal'}
              </h3>
              <p className="text-gray-600">
                {editingId ? 'Update your meal information' : 'Add a new meal to your database'}
              </p>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Meal Name</label>
                  <input 
                    name="name" 
                    placeholder="e.g., Chicken Breast, Greek Salad..."
                    value={form.name} 
                    onChange={handleChange} 
                    required 
                    className="w-full p-4 border border-emerald-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-200 bg-white/80"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <Flame className="inline w-4 h-4 mr-1 text-orange-500" />
                    Calories
                  </label>
                  <input 
                    name="calories" 
                    placeholder="250" 
                    value={form.calories} 
                    onChange={handleChange} 
                    required 
                    type="number" 
                    min="0"
                    className="w-full p-4 border border-emerald-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-200 bg-white/80"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <Drumstick className="inline w-4 h-4 mr-1 text-purple-500" />
                    Protein (g)
                  </label>
                  <input 
                    name="protein" 
                    placeholder="25" 
                    value={form.protein} 
                    onChange={handleChange} 
                    required 
                    type="number" 
                    min="0"
                    step="0.1"
                    className="w-full p-4 border border-emerald-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-200 bg-white/80"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <Wheat className="inline w-4 h-4 mr-1 text-yellow-500" />
                    Carbs (g)
                  </label>
                  <input 
                    name="carbs" 
                    placeholder="15" 
                    value={form.carbs} 
                    onChange={handleChange} 
                    required 
                    type="number" 
                    min="0"
                    step="0.1"
                    className="w-full p-4 border border-emerald-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-200 bg-white/80"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <Droplet className="inline w-4 h-4 mr-1 text-pink-500" />
                    Fat (g)
                  </label>
                  <input 
                    name="fat" 
                    placeholder="8" 
                    value={form.fat} 
                    onChange={handleChange} 
                    required 
                    type="number" 
                    min="0"
                    step="0.1"
                    className="w-full p-4 border border-emerald-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-200 bg-white/80"
                  />
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-3 pt-4">
                <button 
                  type="submit" 
                  className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white px-6 py-4 rounded-xl font-semibold transition-all duration-200 hover:shadow-lg hover:scale-105 active:scale-95 flex items-center justify-center gap-2"
                >
                  <Plus size={20} />
                  {editingId ? 'Update Meal' : 'Add Meal'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setForm({ name: '', calories: '', protein: '', carbs: '', fat: '' });
                    setEditingId(null);
                  }}
                  className="flex-1 sm:flex-initial bg-gray-100 hover:bg-gray-200 text-gray-700 px-6 py-4 rounded-xl font-semibold transition-all duration-200 hover:scale-105 active:scale-95"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Meals;
